import React, { useRef, useMemo, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  ContactShadows,
  PresentationControls,
  Float,
  useGLTF,
  Environment,
  MeshReflectorMaterial,
  Sparkles,
} from "@react-three/drei";
import * as THREE from "three";

// Free Ferrari GLB hosted by three.js — reliable public CDN
const GLB_URL = "https://threejs.org/examples/models/gltf/ferrari.glb";

class GLBErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { console.warn("GLB failed to load, using procedural car:", err?.message); }
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

/**
 * Enhanced GLB car — the ferrari.glb ships with named materials for body, chassis,
 * wheels, glass, interior. We swap the body + rims with physically-based materials
 * to give it that shiny showroom car look.
 */
function FerrariGLB({ color = "#0a0a0d" }) {
  const { scene } = useGLTF(GLB_URL);
  const group = useRef();

  useFrame((state) => {
    if (group.current) {
      group.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.15) * 0.35;
    }
  });

  const enhanced = useMemo(() => {
    const cloned = scene.clone(true);
    cloned.traverse((o) => {
      if (!o.isMesh) return;
      o.castShadow = true;
      o.receiveShadow = true;
      const name = (o.material?.name || o.name || "").toLowerCase();
      // Car body — deep glossy paint
      if (name.includes("body") || name.includes("coat")) {
        o.material = new THREE.MeshPhysicalMaterial({
          color,
          metalness: 1,
          roughness: 0.22,
          clearcoat: 1,
          clearcoatRoughness: 0.05,
          envMapIntensity: 1.6,
        });
      // Glass windows
      } else if (name.includes("glass") || name.includes("window") || name.includes("windshield")) {
        o.material = new THREE.MeshPhysicalMaterial({
          color: "#0d1116",
          metalness: 0.9,
          roughness: 0.05,
          transmission: 0.5,
          thickness: 0.5,
          envMapIntensity: 1.4,
        });
      // Chrome / rim / trim
      } else if (name.includes("rim") || name.includes("chrome") || name.includes("trim") || name.includes("wheel")) {
        o.material = new THREE.MeshPhysicalMaterial({
          color: "#e8e8ec",
          metalness: 1,
          roughness: 0.18,
          clearcoat: 1,
          clearcoatRoughness: 0.1,
          envMapIntensity: 1.6,
        });
      // Tires / rubber / plastic dark parts
      } else if (name.includes("rubber") || name.includes("tire") || name.includes("tyre")) {
        o.material = new THREE.MeshStandardMaterial({
          color: "#050505",
          metalness: 0.1,
          roughness: 0.95,
        });
      }
    });
    return cloned;
  }, [scene, color]);

  return (
    <group ref={group} position={[0, -0.5, 0]} scale={2.2}>
      <primitive object={enhanced} />
    </group>
  );
}
useGLTF.preload(GLB_URL);

/**
 * Reflective showroom floor — the "wet asphalt" look that makes cars look shiny.
 */
function ShowroomFloor() {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.51, 0]} receiveShadow>
      <planeGeometry args={[40, 40]} />
      <MeshReflectorMaterial
        blur={[300, 100]}
        resolution={1024}
        mixBlur={1}
        mixStrength={45}
        roughness={1}
        depthScale={1.2}
        minDepthThreshold={0.4}
        maxDepthThreshold={1.4}
        color="#0a0a0d"
        metalness={0.6}
        mirror={0.65}
      />
    </mesh>
  );
}
/**
 * Fallback procedural car — used if the GLB fails to load.
 * Same look as before but with shinier physical materials.
 */
function StylizedCar({ color = "#0a0a0c" }) {
  const group = useRef();
  useFrame((state) => {
    if (group.current) {
      group.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.15) * 0.35;
    }
  });

  const paint = useMemo(
    () => new THREE.MeshPhysicalMaterial({
      color,
      metalness: 1,
      roughness: 0.22,
      clearcoat: 1,
      clearcoatRoughness: 0.05,
      envMapIntensity: 1.6,
    }),
    [color]
  );
  const glass = useMemo(
    () => new THREE.MeshPhysicalMaterial({
      color: "#0a0d10",
      metalness: 0.9,
      roughness: 0.05,
      transmission: 0.5,
      thickness: 0.5,
      envMapIntensity: 1.4,
    }),
    []
  );
  const tire = useMemo(() => new THREE.MeshStandardMaterial({ color: "#050505", roughness: 0.95 }), []);
  const rim = useMemo(() => new THREE.MeshPhysicalMaterial({ color: "#e8e8ec", metalness: 1, roughness: 0.18, clearcoat: 1 }), []);
  const brake = useMemo(() => new THREE.MeshStandardMaterial({ color: "#FF3B30", emissive: "#5a0f0c", emissiveIntensity: 0.3 }), []);
  const headlight = useMemo(() => new THREE.MeshStandardMaterial({ color: "#ffffff", emissive: "#ffffff", emissiveIntensity: 1.4 }), []);
  const tailLight = useMemo(() => new THREE.MeshStandardMaterial({ color: "#ff3b30", emissive: "#ff3b30", emissiveIntensity: 1.4 }), []);

  const Wheel = ({ position }) => (
    <group position={position}>
      <mesh rotation={[Math.PI / 2, 0, 0]} material={tire}>
        <cylinderGeometry args={[0.42, 0.42, 0.28, 40]} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0.001]} material={rim}>
        <cylinderGeometry args={[0.28, 0.28, 0.3, 32]} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0.002]} material={brake}>
        <torusGeometry args={[0.32, 0.03, 8, 32]} />
      </mesh>
    </group>
  );

  return (
    <group ref={group} position={[0, -0.35, 0]}>
      <mesh position={[0, 0.38, 0]} material={paint}><boxGeometry args={[3.6, 0.55, 1.5]} /></mesh>
      <mesh position={[1.85, 0.32, 0]} material={paint} rotation={[0, 0, -0.05]}><boxGeometry args={[0.6, 0.45, 1.42]} /></mesh>
      <mesh position={[-1.85, 0.34, 0]} material={paint} rotation={[0, 0, 0.05]}><boxGeometry args={[0.55, 0.48, 1.44]} /></mesh>
      <mesh position={[-0.15, 0.85, 0]} material={paint}><boxGeometry args={[1.9, 0.42, 1.3]} /></mesh>
      <mesh position={[0.75, 0.78, 0]} material={glass} rotation={[0, 0, -0.55]}><boxGeometry args={[0.9, 0.42, 1.28]} /></mesh>
      <mesh position={[-1.05, 0.78, 0]} material={glass} rotation={[0, 0, 0.5]}><boxGeometry args={[0.9, 0.42, 1.28]} /></mesh>
      <mesh position={[-0.15, 0.88, 0.66]} material={glass}><boxGeometry args={[1.7, 0.38, 0.02]} /></mesh>
      <mesh position={[-0.15, 0.88, -0.66]} material={glass}><boxGeometry args={[1.7, 0.38, 0.02]} /></mesh>
      <mesh position={[2.14, 0.42, 0.5]} material={headlight}><boxGeometry args={[0.05, 0.08, 0.35]} /></mesh>
      <mesh position={[2.14, 0.42, -0.5]} material={headlight}><boxGeometry args={[0.05, 0.08, 0.35]} /></mesh>
      <mesh position={[-2.13, 0.44, 0.5]} material={tailLight}><boxGeometry args={[0.04, 0.1, 0.4]} /></mesh>
      <mesh position={[-2.13, 0.44, -0.5]} material={tailLight}><boxGeometry args={[0.04, 0.1, 0.4]} /></mesh>
      <Wheel position={[1.25, 0.12, 0.78]} />
      <Wheel position={[1.25, 0.12, -0.78]} />
      <Wheel position={[-1.25, 0.12, 0.78]} />
      <Wheel position={[-1.25, 0.12, -0.78]} />
    </group>
  );
}

export default function Car3D({ color = "#0a0a0d", interactive = true }) {
  return (
    <Canvas
      shadows
      dpr={[1, 2]}
      camera={{ position: [4.5, 1.6, 5.4], fov: 30 }}
      gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
    >
      <fog attach="fog" args={["#050505", 16, 30]} />

      {/* Cinematic lighting */}
      <ambientLight intensity={0.25} />
      <directionalLight
        position={[6, 10, 4]}
        intensity={1.6}
        castShadow
        shadow-mapSize={[1024, 1024]}
        shadow-bias={-0.0001}
      />
      <directionalLight position={[-8, 6, -3]} intensity={0.8} color="#FF3B30" />
      <spotLight position={[0, 8, 5]} intensity={0.9} angle={0.4} penumbra={0.6} color="#ffffff" />
      <pointLight position={[-4, 1, 4]} intensity={0.5} color="#4488ff" />

      <PresentationControls
        enabled={interactive}
        global
        cursor
        snap
        rotation={[0, 0.35, 0]}
        polar={[-0.2, 0.2]}
        azimuth={[-Math.PI / 2, Math.PI / 2]}
        config={{ mass: 1, tension: 170, friction: 26 }}
      >
        <Float speed={1.2} rotationIntensity={0.08} floatIntensity={0.2}>
          <GLBErrorBoundary fallback={<StylizedCar color={color} />}>
            <Suspense fallback={<StylizedCar color={color} />}>
              <FerrariGLB color={color} />
            </Suspense>
          </GLBErrorBoundary>
        </Float>
      </PresentationControls>

      {/* Reflective floor for that "wet showroom" shine */}
      <Suspense fallback={null}>
        <ShowroomFloor />
      </Suspense>

      <ContactShadows
        position={[0, -0.5, 0]}
        opacity={0.9}
        scale={14}
        blur={2.6}
        far={8}
        resolution={512}
        color="#000000"
      />

      {/* Realistic HDR environment gives the car its reflective highlights */}
      <Suspense fallback={null}>
        <Environment preset="city" background={false} resolution={256} />
      </Suspense>

      {/* Tiny floating specks for cinematic mood */}
      <Sparkles count={30} scale={[10, 4, 10]} size={2} speed={0.3} opacity={0.4} color="#FF3B30" />
    </Canvas>
  );
}
